'use client';

import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { Button, Icon } from '@ui';
import { FC } from 'react';
import useFormatDate from '@/hooks/use-format-date';
import { useTranslation } from '@/providers/app-provider/app-provider';
import Review, { ReviewType } from '../review/review';

type Props = {
  review: ReviewType;
  removalReason: string;
  removedAtRaw?: Date | string;
  onCreateNew: () => void;
};

const RemovedReviewBanner: FC<Props> = ({ review, removalReason, removedAtRaw, onCreateNew }) => {
  const { t, tFormat } = useTranslation();
  const { formatDateTime } = useFormatDate();

  const removedAt = removedAtRaw ? formatDateTime(removedAtRaw) : undefined;
  const trimmedReason = removalReason.trim();
  const firstBreak = trimmedReason.search(/\r?\n/);
  const reasonHeading =
    firstBreak === -1 ? trimmedReason : trimmedReason.slice(0, firstBreak).trim();
  const reasonBody = firstBreak === -1 ? '' : trimmedReason.slice(firstBreak + 1).trim();

  return (
    <div className="rounded-medium overflow-hidden">
      <div className="bg-danger text-small flex items-center gap-2 px-4 py-3 font-medium text-white">
        <Icon icon={faCircleInfo} />
        {t?.productDetail.addReviewModal.reviewRemovedBanner}
      </div>
      <div className="bg-default-100 grid gap-6 p-5 md:grid-cols-2 md:gap-32">
        <Review {...review} />
        <div className="flex flex-col gap-3">
          <div className="text-danger text-small flex items-center gap-2 font-semibold tracking-wide uppercase">
            <span className="bg-danger inline-block h-2 w-2 rounded-full" />
            {t?.productDetail.addReviewModal.removalReasonLabel}
          </div>
          {reasonHeading && <p className="text-default-900 font-semibold">{reasonHeading}</p>}
          {reasonBody && <p className="text-default-700 whitespace-pre-line">{reasonBody}</p>}
          {removedAt && (
            <p className="text-default-400 text-small">
              {tFormat(t?.productDetail.addReviewModal.removedAt, { date: removedAt })}
            </p>
          )}
          <Button className="mt-2 self-start bg-black text-white" onClick={onCreateNew}>
            {t?.productDetail.addReviewModal.createNewReview}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RemovedReviewBanner;
